import React, { memo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import EmailInputAndFileUpload from './email-input-and-file-upload';
import { RootState } from '../../../store/root-reducer';
import {
  resetVerifyEmailResuest,
  searchPaginationState,
} from '../email-verifier-slice';
import { enableBulkActionRequestPolling } from '../../home/home-slice';
import {
  emailVerifierUploadedFilesListRequest,
  getVerifyEmailRequest,
} from '../extra-action';
import { GetUploadedFilePayload, VerifyEmailRequestPayload } from '../types';
import { getEmailVerificationCreditsRequest } from '../../settings/components/billing-subscription/extra-actions';
import { ExceptionTypes } from '../../../shared/types';

const EmailInputAndFileUploadContainer: React.FC = memo(() => {
  // ** Ref
  const emailVerifyListCancelTokenRef = useRef<any>(null);

  // ** Redux Hooks **
  const dispatch = useDispatch();

  // ** Redux Selectors **
  const emailVerifierRequestStatus = useSelector(
    (state: RootState) => state?.emailVerifier?.getVerifyEmailRequest,
  );

  const emailVerifyCredits = useSelector(
    (state: RootState) =>
      state?.billingAndSubscription?.getEmailVerificationCreditsResponse,
  );
  const isVerifiedEmailData = useSelector(
    (state: RootState) => state?.emailVerifier?.isVerifiedEmailData,
  );
  const { total: totalEmailVerifierUploadedFilesList } = useSelector(
    (state: RootState) => state?.emailVerifier?.emailVerifierUploadedFilesList,
  );

  const responseData = useSelector(
    (state: RootState) =>
      state?.emailVerifier?.emailVerifierUploadedFilesListRequest,
  );

  const shouldBulkActionPoll = useSelector(
    (state: RootState) => state?.home?.pollingNotifications,
  );

  const resetVerifyEmailResuests = useCallback(() => {
    dispatch(resetVerifyEmailResuest());
  }, [dispatch]);

  const enableBulkActionRequestPollings = useCallback(() => {
    dispatch(enableBulkActionRequestPolling());
  }, [dispatch]);

  const emailVerifierUploadedFilesListRequests = useCallback(
    (payload: GetUploadedFilePayload) => {
      dispatch(emailVerifierUploadedFilesListRequest(payload));
    },
    [dispatch],
  );

  const getEmailVerificationCreditsRequests = useCallback(() => {
    dispatch(getEmailVerificationCreditsRequest());
  }, [dispatch]);

  const getVerifyEmailRequests = useCallback(
    (payload: VerifyEmailRequestPayload) => {
      dispatch(getVerifyEmailRequest(payload));
    },
    [dispatch],
  );

  const setSearchPagination = useCallback((payload: GetUploadedFilePayload) => {
    dispatch(searchPaginationState(payload));
  }, []);

  // ** Handlers
  const getEmailVerifierUploadedFilesListRequest = useCallback(
    (payload: GetUploadedFilePayload) => {
      // ** Cancel Previous Request
      if (emailVerifyListCancelTokenRef?.current) {
        emailVerifyListCancelTokenRef?.current?.cancel({
          type: ExceptionTypes.Silent,
          message: 'EMAIL_VERIFY_LIST_CANCELLED',
        });
        emailVerifyListCancelTokenRef.current = null;
      }

      emailVerifyListCancelTokenRef.current = axios?.CancelToken?.source();
      const cancelToken = emailVerifyListCancelTokenRef?.current?.token || null;

      // ** Dispatch Request
      dispatch(
        emailVerifierUploadedFilesListRequest({
          ...payload,
          ...(cancelToken ? { cancelToken } : {}),
        }),
      );
    },
    [dispatch],
  );

  return (
    <EmailInputAndFileUpload
      emailVerifierRequestStatus={emailVerifierRequestStatus}
      emailVerifyCredits={emailVerifyCredits}
      isVerifiedEmailData={isVerifiedEmailData}
      totalEmailVerifierUploadedFilesList={totalEmailVerifierUploadedFilesList}
      responseData={responseData}
      resetVerifyEmailResuests={resetVerifyEmailResuests}
      enableBulkActionRequestPollings={enableBulkActionRequestPollings}
      emailVerifierUploadedFilesListRequests={
        emailVerifierUploadedFilesListRequests
      }
      getEmailVerificationCreditsRequests={getEmailVerificationCreditsRequests}
      getVerifyEmailRequests={getVerifyEmailRequests}
      getEmailVerifierUploadedFilesListRequest={
        getEmailVerifierUploadedFilesListRequest
      }
      setSearchPagination={setSearchPagination}
      shouldBulkActionPoll={shouldBulkActionPoll}
    />
  );
});

export default EmailInputAndFileUploadContainer;
